/**
 * CC8Share — share + upsell screen.
 * data-screen-label="cc-08 Upsell + share"
 *
 * - 9:16 share card preview at 200px wide
 * - Couple Portrait upsell tile linking to /sku/couple_portrait
 * - "Vasya was notified" pill if partner was invited (wasInvited from flow state)
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SKU_CATALOG } from '@wds/astro-bot-shared/catalog';
import WebApp from '@twa-dev/sdk';
import { CCBackdrop, CCAvatar } from './shared';
import { useChart } from '../../../hooks/useChart';
import { useImageFeatures } from '../../../hooks/useImageFeatures';
import { AppHeader } from '../../chrome/AppHeader';
import { PrimaryButton } from '../../chrome/Button';
import type { UseCompatFlowReturn } from '../../../hooks/useCompatFlow';
import styles from './Compat.module.css';

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

interface CC8ShareProps {
  flow: UseCompatFlowReturn;
}

export function CC8Share({ flow }: CC8ShareProps): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation('flows');
  const { partner, output, wasInvited, searchUsername } = flow;
  // Own archetype comes from the natal chart (the flow only carries the partner).
  const { data: chart } = useChart();
  const { enabled: imagesOn } = useImageFeatures();
  const myArchetype = chart?.status === 'ready' ? chart.archetype?.label ?? null : null;

  const overall = output ? Math.round(output.scoreOverall) : 87;
  const myGlyph = '🌙';
  const partnerGlyph = partner?.glyph ?? '✦';
  const partnerPalette = partner?.palette ?? 'violet';

  function handleSendStory() {
    // Share to Telegram story
    try {
      WebApp.shareToStory?.('', { text: t('compat.share.storyText', { pct: overall }) });
    } catch {
      // No-op in non-Telegram environments
    }
  }

  function handleUpsell() {
    const username = partner?.username ?? searchUsername;
    const dest = username
      ? `/flow/couple?partnerUsername=${encodeURIComponent(username)}&upsell=1`
      : '/flow/couple?upsell=1';
    void navigate(dest);
  }

  return (
    <div className={styles.root} data-screen-label="cc-08 Upsell + share">
      <CCBackdrop tone="pair" />

      <div className={styles.content}>
        {/* Nav */}
        <AppHeader title={t('flows:compat.headerTitle')} onBack={() => navigate(-1)} headingId="compat-heading" />

        {/* Title */}
        <div style={{ marginTop: 12, textAlign: 'center' }}>
          <div className={styles.eyebrow}>{t('compat.share.eyebrow')}</div>
          <h1 className={styles.displayTitleCenter}>
            {t('compat.share.titlePrefix')}<em className={styles.gradAccent}>{t('compat.share.titleAccent')}</em>{t('compat.share.titleSuffix')}
          </h1>
        </div>

        {/* Share card preview — 9:16 at 200px wide */}
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center' }}>
          <div
            className={[styles.shareCardPreview, styles.shareCardBg].join(' ')}
            aria-label={t('compat.share.previewAria')}
          >
            {/* Top row */}
            <div className={styles.shareCardTopRow}>
              <span className={styles.shareCardLogo}>Calestea ✦</span>
              <span>{overall}%</span>
            </div>

            {/* Avatars */}
            <div className={styles.shareCardAvatars}>
              <div style={{ transform: 'rotate(-6deg)' }}>
                <CCAvatar glyph={myGlyph} palette="gold" size={68} />
              </div>
              <div style={{ transform: 'rotate(6deg) translateX(-10px)' }}>
                <CCAvatar glyph={partnerGlyph} palette={partnerPalette} size={68} />
              </div>
            </div>

            {/* Bottom */}
            <div className={styles.shareCardBottom}>
              <div className={styles.shareCardWeAre}>{t('compat.share.weAre')}</div>
              <div className={styles.shareCardTitle}>
                {myArchetype ?? t('compat.pair.me')} <em className={styles.gradAccent}>×</em> {partner?.archetype ?? t('compat.pair.partnerFallback')}
              </div>
              <div className={styles.shareCardHandles}>
                /c/me × /c/{partner?.username ?? searchUsername}
              </div>
            </div>
          </div>
        </div>

        {/* Couple Portrait upsell */}
        {imagesOn && (
          <button
            type="button"
            className={styles.upsellCard}
            onClick={handleUpsell}
            data-testid="cc8-upsell-btn"
          >
            <div className={styles.upsellIcon} aria-hidden="true">❀</div>
            <div>
              <div className={styles.upsellTitle}>{t('compat.share.upsellTitle')}</div>
              <div className={styles.upsellSub}>{t('compat.share.upsellSub')}</div>
            </div>
            <div className={styles.upsellPrice}>{t('compat.share.upsellPrice', { stars: SKU_CATALOG['couple_portrait_upsell'].stars })}</div>
          </button>
        )}

        {/* "Partner notified" pill — only shown if invited */}
        {wasInvited && (
          <div className={styles.notifiedPill} role="status">
            <span className={styles.notifiedCheck} aria-hidden="true">✓</span>
            {partner?.displayName ?? searchUsername} {t('compat.share.notified')}
          </div>
        )}

        {/* CTA */}
        <div className={styles.ctaArea}>
          <PrimaryButton
            onClick={handleSendStory}
            data-testid="cc8-send-story-btn"
          >
            ↗ {t('compat.share.sendStory')}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
